import moment from "moment";
import CenterPhoneList from "../components/center-phone/CenterPhoneList";
import http from "../http-common";

/** Lean Mongo rows expose `_id`; app code expects `id` (same as Mongoose toJSON). */
const normalizeEventRecord = (event) => {
  if (!event || typeof event !== "object") return event;
  if (event.id != null && `${event.id}`.trim() !== "") return event;
  if (event._id == null) return event;
  return { ...event, id: String(event._id) };
};

const normalizeEventListResponse = (response) => {
  if (Array.isArray(response?.data)) {
    response.data = response.data.map(normalizeEventRecord);
  }
  return response;
};

const normalizeEventResponse = (response) => {
  if (response?.data) {
    response.data = normalizeEventRecord(response.data);
  }
  return response;
};

/** Schedule-X requires a non-empty string id on every event (uses it in querySelector). */
const prepareEventsForScheduleX = (events) => {
  if (!Array.isArray(events)) return [];
  return events
    .map(normalizeEventRecord)
    .filter((ev) => {
      const id = ev?.id;
      return id != null && `${id}`.trim() !== "";
    });
};

const getAllEvents = (params, config = {}) => {
  return http.get('/events', { params, ...config }).then(normalizeEventListResponse);
};

const exportMedicalOneDayList = (date, status = "active") => {
  return http.get("/events/export-medical-one-day-list", {
    params: { date, status },
    responseType: "blob",
  });
};

const getByCustomer = (params) => {
  return http.get('/events/getByCustomer', { params }).then(normalizeEventListResponse);
};

/** True if customer already has an active medical appointment on the same local calendar day. */
const customerHasMedicalAppointmentOnDate = async (customerId, date) => {
  if (!customerId || !date) return false;
  const dateValue = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dateValue.getTime())) return false;

  const response = await getAllEvents({
    date: formatDate(dateValue),
    type: 'medical',
  });
  const events = Array.isArray(response?.data) ? response.data : [];
  const idStr = String(customerId);

  return events.some((event) => {
    if (event?.status === 'inactive') return false;
    const candidates = [event?.data?.customer, event?.target_uuid];
    return candidates.some((candidate) => {
      if (candidate == null || candidate === '') return false;
      if (typeof candidate === 'object') {
        return String(candidate._id || candidate.id || '') === idStr;
      }
      return String(candidate) === idStr;
    });
  });
};

const createNewEvent = (data) => {
  data.status = 'active';
	return http.post('/events', data);
};


const updateEvent = (id, data) => {
  return http.put(`/events/${id}`, data);
}

const disableEvent = (id, data) => {
  return http.put(`/events/${id}/disable`, data);
}

const getEvent = (id) => {
  return http.get(`/events/${id}`).then(normalizeEventResponse);
}

const deleteEvent = (id) => {
  return http.delete(`/events/${id}`)
}

const assignTransportationToEvents = (data) => {
  return http.post(`/events/assign`, data);
}

// Event Recurrence API
const getAllEventRecurrences = () => {
  return http.get('/event-recurrences');
};

const createEventRecurrence = (data) => {
  return http.post('/event-recurrences', data);
};

const updateEventRecurrence = (id, data) => {
  return http.put(`/event-recurrences/${id}`, data);
};

const disableEventRecurrence = (id, data) => {
  return http.put(`/event-recurrences/${id}/disable`, data);
};

const deleteEventRecurrence = (id) => {
  return http.delete(`/event-recurrences/${id}`);
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const localDate = `${year}-${month}-${day}`;
  return localDate;
};

const getTimeData = () => {
  return http.get(`/timedata/get-by-condition1`, {
      params: {
        date: moment(new Date()).format('YYYY-MM-DD')
      }
    });
}

/**
 * Build once per medical calendar refresh; use getTransportationInfoFromContext per row (O(1) lookups).
 */
const buildMedicalTransportationContext = (eventsList, timeDocs = []) => {
  const today = new Date();
  const futureEvents = eventsList.filter(
    (d) => new Date(d.start_time) >= new Date(today.toLocaleDateString())
  );
  const futureIds = new Set(futureEvents.map((e) => e.id));
  const futureEventsGroupMap = new Map();
  for (const e of futureEvents) {
    const eDateString = new Date(e.start_time).toLocaleDateString();
    if (futureEventsGroupMap.has(eDateString)) {
      futureEventsGroupMap.get(eDateString).push(e);
    } else {
      futureEventsGroupMap.set(eDateString, [e]);
    }
  }
  const timeDocByYmd = new Map();
  for (const timeDoc of timeDocs || []) {
    const ymd = moment(timeDoc.time).format('YYYY-MM-DD');
    timeDocByYmd.set(ymd, timeDoc);
  }
  return { futureEventsGroupMap, futureIds, timeDocByYmd };
};

const getTransportationInfoFromContext = (item, ctx) => {
  const { futureEventsGroupMap, futureIds, timeDocByYmd } = ctx;
  const ymd = moment(item.start_time).format('YYYY-MM-DD');
  const td = timeDocByYmd.get(ymd);
  const dayKey = new Date(item?.start_time).toLocaleDateString();
  const daySlice = futureEventsGroupMap.get(dayKey);
  return {
    maxTranslate1: td?.translate1_number || 0,
    maxTranslate2: td?.translate2_number || 0,
    maxResource: td?.max_resource_number || 0,
    totalTranslate1: daySlice?.filter((e) => e.interpreter === '1(译)')?.length,
    totalTranslate2: daySlice?.filter((e) => e.interpreter === '2(译)')?.length,
    totalResource: daySlice
      ? [...new Set(daySlice.map((e) => e.source_uuid))].length
      : undefined,
    isFutureEvent: futureIds.has(item.id),
  };
};

const getTransportationInfo = (eventsList, item, timeDocs = []) => {
  const ctx = buildMedicalTransportationContext(eventsList, timeDocs);
  return getTransportationInfoFromContext(item, ctx);
};

// Language Support options
const languageSupportOptions = [
  {
    value: 'By Default',
    label: 'By Default'
  },
  {
    value: 'Center',
    label: 'Center',
  },
  {
    value: 'Family',
    label: 'Family'
  },
  {
    value: 'Office',
    label: 'Office in person'
  },
  {
    value: 'Office(Phone)',
    label: 'Office by phone'
  },
  {
    value: 'Nurse',
    label: 'Nurse'
  }
];

const LANGUAGE_SUPPORT_BY_DEFAULT_KEYS = new Set([
  'by default',
  'checkin',
  'check in',
  'no',
]);

const normalizeLanguageSupportKey = (value) =>
  `${value || ''}`.trim().toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ');

function isLanguageSupportByDefault(value) {
  return LANGUAGE_SUPPORT_BY_DEFAULT_KEYS.has(normalizeLanguageSupportKey(value));
}

function normalizeLanguageSupportSelectValue(raw) {
  const v = `${raw ?? ''}`.trim();
  if (!v) return '';
  if (isLanguageSupportByDefault(v)) return 'By Default';
  const match = languageSupportOptions.find(
    (o) => normalizeLanguageSupportKey(o.value) === normalizeLanguageSupportKey(v)
      || normalizeLanguageSupportKey(o.label) === normalizeLanguageSupportKey(v)
  );
  return match ? match.value : v;
}

function getMedicalLanguageSupportDisplay(raw) {
  const v = `${raw ?? ''}`.trim();
  if (!v) return '';
  if (isLanguageSupportByDefault(v)) return 'By Default';
  const match = languageSupportOptions.find(
    (o) => normalizeLanguageSupportKey(o.value) === normalizeLanguageSupportKey(v)
      || normalizeLanguageSupportKey(o.label) === normalizeLanguageSupportKey(v)
  );
  return match ? match.label : v;
}

// Legacy alias for backward compatibility
const interpreterLevelOptions = languageSupportOptions;

// Label options for Medical Events
const labelOptions = [
  {
    value: 'red',
    label: 'Eyes-On'
  },
  {
    value: 'pink',
    label: 'Self-Transport',
  },
  {
    value: 'green',
    label: 'Televisit'
  },
  {
    value: 'blue',
    label: 'Default for By Center'
  },
  {
    value: 'black',
    label: 'Client Does Not need to Go'
  },
  {
    value: 'purple',
    label: 'Drop-Off Only',
  },
  {
    value: 'gray',
    label: 'Deleted Appt'
  },
  {
    value: 'orange',
    label: 'Pick-Up Only'
  },
  {
    value: 'brown',
    label: 'Translation by Center'
  }
];

// Activity color options
const activityColorOptions = [
  {
    value: 'red',
    label: 'Classes'
  },
  {
    value: 'pink',
    label: 'Games'
  },
  {
    value: 'green',
    label: 'Events'
  },
  {
    value: 'blue',
    label: 'Outings'
  },
  {
    value: 'purple',
    label: 'Personal Care'
  },
  {
    value: 'brown',
    label: 'Care Activities'
  }
];

// Incident/Attendance Notes color options (always blue)
const incidentColorOptions = [
  {
    value: 'blue',
    label: 'Attendance Note'
  }
];

// Meal Plan color options
const mealPlanColorOptions = [
  {
    value: 'brown',
    label: 'Breakfast'
  },
  {
    value: 'green',
    label: 'Lunch'
  },
  {
    value: 'red',
    label: 'Snack'
  }
];

// Legacy alias for backward compatibility
const colorOptions = labelOptions;

// Important Dates legend options (two visual groups)
const importantDatesLegendOptions = [
  {
    value: 'member_related',
    label: 'Member-Related'
  },
  {
    value: 'vehicle_maintenance',
    label: 'Vehicle Maintenance'
  }
];

// Important Dates filter options (matching the title dropdown in the creation popup)
const importantDatesFilterOptions = [
  {
    value: 'birthday',
    label: 'Birthday',
    group: 'member_related'
  },
  {
    value: 'adcaps_completion',
    label: 'ADCAPS Completed Date',
    group: 'member_related'
  },
  {
    value: 'id_expiration_date',
    label: 'ID Expiration Date',
    group: 'member_related'
  },
  {
    value: 'center_qualification_expiration',
    label: 'Center Qualification Expiration',
    group: 'member_related'
  },
  {
    value: 'oil_change',
    label: 'Oil Change',
    group: 'vehicle_maintenance'
  },
  {
    value: 'monthly_vehicle_inspection',
    label: 'Monthly Vehicle Inspection',
    group: 'vehicle_maintenance'
  },
  {
    value: 'emissions_inspection',
    label: 'Emissions',
    group: 'vehicle_maintenance'
  },
  {
    value: 'insurance_expiration',
    label: 'Insurance Expiration',
    group: 'vehicle_maintenance'
  },
  {
    value: 'license_plate_expiration',
    label: 'Vehicle Registration Date',
    group: 'vehicle_maintenance'
  },
  {
    value: 'first_aid_kit',
    label: 'First Aid Kit',
    group: 'vehicle_maintenance'
  },
  {
    value: 'other',
    label: 'Other',
    group: 'other'
  }
];

// Transportation Support options
const transportationTypeOptions = [
  {
    value: 'Center Transportation',
    label: 'Default for By Center'
  },
  {
    value: 'by own',
    label: 'Self-Transport'
  },
  {
    value: 'televisit',
    label: 'Televisit'
  },
  {
    value: 'client does not need to go',
    label: 'Client Does Not need to Go'
  },
  {
    value: 'dropoff only',
    label: 'Drop-Off Only'
  },
  {
    value: 'pickup only',
    label: 'Pick-Up Only'
  },
  {
    value: 'no',
    label: 'Friendly Reminder'
  }
];

const TRANSPORT_SUPPORT_DISPLAY_ALIASES = {
  'by own': 'Self-Transport',
  'byown': 'Self-Transport',
  'self transport': 'Self-Transport',
  'self-transport': 'Self-Transport',
  'center transportation': 'Default for By Center',
  'by center transportation': 'Default for By Center',
  'default for by center': 'Default for By Center',
  'dropoff only': 'Drop-Off Only',
  'drop off only': 'Drop-Off Only',
  'drop-off only': 'Drop-Off Only',
  'pickup only': 'Pick-Up Only',
  'pick up only': 'Pick-Up Only',
  'pick-up only': 'Pick-Up Only',
  'no': 'Friendly Reminder',
  'friendly reminder': 'Friendly Reminder',
};

const normalizeTransportSupportKey = (value) =>
  `${value || ''}`.trim().toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ');

function isTransportationSupportByCenterDefault(value) {
  const key = normalizeTransportSupportKey(value);
  return !key
    || key === 'center transportation'
    || key === 'by center transportation'
    || key === 'default for by center';
}

function normalizeTransportationSupportSelectValue(raw) {
  if (isTransportationSupportByCenterDefault(raw)) return 'Center Transportation';
  const v = `${raw ?? ''}`.trim();
  const exact = transportationTypeOptions.find((o) => `${o.value}` === v);
  if (exact) return exact.value;
  const key = normalizeTransportSupportKey(v);
  const byValue = transportationTypeOptions.find((o) => normalizeTransportSupportKey(o.value) === key);
  if (byValue) return byValue.value;
  const byLabel = transportationTypeOptions.find((o) => normalizeTransportSupportKey(o.label) === key);
  return byLabel ? byLabel.value : v;
}

/** When `trans_method` is empty, UI lists show the same default as explicit "Center Transportation". */
function getMedicalTransportationSupportDisplay(rawTransMethod) {
  const v = `${rawTransMethod ?? ''}`.trim();
  if (!v) return 'Default for By Center';
  const exact = transportationTypeOptions.find((o) => `${o.value}` === v);
  if (exact) return exact.label;
  const key = normalizeTransportSupportKey(v);
  const byValue = transportationTypeOptions.find((o) => normalizeTransportSupportKey(o.value) === key);
  if (byValue) return byValue.label;
  const compact = key.replace(/\s+/g, '');
  if (TRANSPORT_SUPPORT_DISPLAY_ALIASES[key]) return TRANSPORT_SUPPORT_DISPLAY_ALIASES[key];
  if (TRANSPORT_SUPPORT_DISPLAY_ALIASES[compact]) return TRANSPORT_SUPPORT_DISPLAY_ALIASES[compact];
  return v;
}



const hostname = window.location.hostname.toLowerCase();

// Base URL selection by host.
// Supports: ws{n}, site{n}, and worldshine{site_num}.mayo.llc
const isSite1Host =
  hostname.includes('ws1') ||
  hostname.includes('site1') ||
  hostname.includes('localhost') ||
  hostname.includes('worldshine1.mayo.llc') ||
  hostname.includes('worldshine.mayo.llc') ||
  hostname.includes('worldshine1');

const isSite3Host =
  hostname.includes('ws3') ||
  hostname.includes('site3') ||
  hostname.includes('worldshine3.mayo.llc') ||
  hostname.includes('worldshine3');

const baseUrl = isSite1Host
  ? "https://ws1-tspt.mayosolution.com/api"
  : isSite3Host
    ? "https://ws3-tspt.mayosolution.com/api"
    : "https://ws2-tspt.mayosolution.com/api";
// const baseUrl = (window.location.hostname.includes('worldshine.mayo.llc') ||window.location.hostname.includes('worldshine1') || window.location.hostname.includes('site1')) ? "https://worldshine.mayo.llc/api" : ((window.location.hostname.includes('worldshine3') || window.location.hostname.includes('site3')) ? "https://worldshine3.mayo.llc/api" : "https://worldshine2.mayo.llc/api")

const site = isSite1Host ? 1 : isSite3Host ? 3 : 2;
//const site = (window.location.hostname.includes('worldshine.mayo.llc') ||window.location.hostname.includes('worldshine1') || window.location.hostname.includes('site1')) ? 1 : ((window.location.hostname.includes('worldshine3') || window.location.hostname.includes('site3')) ? 3 : 2)

const generatePdf = (data) => {
  window.open(`${baseUrl}/docs/get-pdfs?docTemplateName=med_notification&inputData=${encodeURIComponent(JSON.stringify(data))}`);
}

const generateSheet = (data) => {
  window.open(`${baseUrl}/sheets/get-sheets?excelTemplateName=visit_record_sheet&inputData=${encodeURIComponent(JSON.stringify(data))}`);
}

const generateDoc = (data) => {
  window.open(`${baseUrl}/docs/get-docs?docTemplateName=med_notification&inputData=${encodeURIComponent(JSON.stringify(data))}`);
}

const generateSheetPDF = (data) => {
  window.open(`${baseUrl}/sheets/get-pdf?excelTemplateName=visit_record_sheet&inputData=${encodeURIComponent(JSON.stringify(data))}`);
}

const medicalEventMatchesFilterValue = (item, filter) => {
  if (item?.isFriendlyReminder) return filter === 'friendly_reminder';
  const colorNorm = normalizeMedicalColor(item?.medicalColorKey || item?.color);
  const icons = Array.isArray(item?.medicalIcons) ? item.medicalIcons : [];
  if (filter === 'friendly_reminder') return false;
  if (filter === 'pickup_dropoff' || filter === 'orange' || filter === 'purple') {
    return colorNorm === 'pickup_dropoff' || MEDICAL_PICKUP_DROPOFF_COLORS.has(colorNorm);
  }
  if (filter === 'red') return icons.includes('eyesOn');
  if (filter === 'teal') return icons.includes('nurseTranslate');
  if (filter === 'brown') return icons.includes('centerTranslate');
  if (filter === 'blue') {
    return !colorNorm || colorNorm === 'blue';
  }
  return colorNorm === filter;
};

/** Medical calendar "Filter by Type" — one pass, status/color normalized (avoids Active vs active, Gray vs gray). */
const matchesMedicalCalendarTypeFilter = (item, selectedColorFilters) => {
  const statusNorm = `${item?.status ?? ''}`.trim().toLowerCase();
  const isActive = statusNorm === 'active';
  const colorNorm = normalizeMedicalColor(item?.color);
  const filters = (selectedColorFilters || []).map((f) => normalizeMedicalColor(f));

  if (filters.length === 0) {
    if (!isActive) return false;
    if (colorNorm === 'gray') return false;
    return true;
  }

  const onlyDeleted = filters.length === 1 && filters[0] === 'gray';
  if (onlyDeleted) {
    return !isActive || colorNorm === 'gray';
  }

  let ok = false;
  if (filters.includes('gray')) {
    if (!isActive || colorNorm === 'gray') ok = true;
  }
  for (const f of filters) {
    if (f === 'gray') continue;
    if (isActive && medicalEventMatchesFilterValue(item, f)) ok = true;
  }
  return ok;
};

/**
 * Same customer + provider + start_time can exist twice (active + inactive) after reschedule/disable.
 * Resolve to one row: for "Deleted Appt" only (gray), keep inactive; otherwise keep active.
 */
const dedupeMedicalDuplicateSlots = (items, selectedColorFilters) => {
  const list = Array.isArray(items) ? items : [];
  const filters = (selectedColorFilters || []).map((f) => `${f}`.trim().toLowerCase());
  const onlyDeleted = filters.length === 1 && filters[0] === 'gray';
  const isActive = (item) => `${item?.status ?? ''}`.trim().toLowerCase() === 'active';
  const slotKey = (item) => {
    const cust =
      item?.data?.customer != null
        ? String(item.data.customer)
        : `${item?.target_uuid ?? ''}`;
    const res =
      item?.data?.resource != null
        ? String(item.data.resource)
        : `${item?.source_uuid ?? ''}`;
    const t = item?.start_time ? new Date(item.start_time).getTime() : 0;
    return `${cust}|${res}|${t}`;
  };
  const byKey = new Map();
  for (const item of list) {
    const k = slotKey(item);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(item);
  }
  const out = [];
  for (const group of byKey.values()) {
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }
    const actives = group.filter(isActive);
    const inactives = group.filter((i) => !isActive(i));
    if (actives.length && inactives.length) {
      if (onlyDeleted) {
        out.push(...inactives);
      } else {
        out.push(...actives);
      }
    } else {
      out.push(...group);
    }
  }
  return out;
};

/** When only "Deleted Appt" (gray) is selected, force gray styling on every tile/list row (same as deleted legend). */
const applyMedicalDeletedOnlyTileStyle = (items, selectedColorFilters) => {
  const list = Array.isArray(items) ? items : [];
  const filters = (selectedColorFilters || []).map((f) => `${f}`.trim().toLowerCase());
  if (filters.length !== 1 || filters[0] !== 'gray') return list;
  return list.map((ev) => ({
    ...ev,
    color: 'gray',
    _options: { additionalClasses: ['event-gray'] },
  }));
};

const normalizeMedicalColor = (color) => {
  const c = `${color ?? ''}`.trim().toLowerCase();
  return c === 'grey' ? 'gray' : c;
};

const MEDICAL_ICON_ONLY_COLORS = new Set(['red', 'brown', 'teal']);
const MEDICAL_PICKUP_DROPOFF_COLORS = new Set(['orange', 'purple', 'pickup_dropoff']);

/** Color used on the calendar tile. Icon-only labels use Default (blue); pick-up and drop-off share one color. */
const medicalEventDisplayColor = (color) => {
  const c = normalizeMedicalColor(color);
  if (MEDICAL_ICON_ONLY_COLORS.has(c)) return 'blue';
  if (MEDICAL_PICKUP_DROPOFF_COLORS.has(c)) return 'pickup_dropoff';
  if (!c) return 'blue';
  return c;
};

/**
 * Medical calendar tile class. Color key comes from Transportation Support (not Label).
 * Pick-Up Only and Drop-Off Only share one class.
 */
const medicalEventTileClassForColor = (color) => {
  const display = medicalEventDisplayColor(color);
  if (display === 'pink') return 'event-medical-self-transport';
  if (display === 'pickup_dropoff') return 'event-medical-pickup-dropoff';
  if (display === 'friendly_reminder') return 'event-medical-friendly-reminder';
  if (!display || display === 'blue') return 'event-blue';
  return `event-${display}`;
};

/** Tile color from Transportation Support. */
const medicalCalendarColorKeyFromTransportation = (transMethod) => {
  const stored = normalizeTransportationSupportSelectValue(transMethod);
  if (stored === 'by own') return 'pink';
  if (stored === 'televisit') return 'green';
  if (stored === 'client does not need to go') return 'black';
  if (stored === 'dropoff only' || stored === 'pickup only') return 'pickup_dropoff';
  if (stored === 'no') return 'friendly_reminder';
  return 'blue';
};

const normalizeProviderText = (value) =>
  `${value || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');

const isFriendlyReminderProvider = (event, resource) => {
  const values = [
    resource?.name,
    resource?.type,
    resource?.type_other,
    event?.source_name,
    event?.source_type,
    event?.data?.resource_name,
    event?.data?.resource_type,
    event?.data?.resource_type_other,
    event?.doctor,
  ];
  return values.some((value) => {
    const normalized = normalizeProviderText(value);
    if (!normalized) return false;
    return normalized.includes('friendly reminder') || normalized.includes('友情提示');
  });
};

const isYesFlag = (value) => {
  const v = `${value ?? ''}`.trim().toLowerCase();
  return v === 'yes' || v === 'true' || value === true;
};

/** Icons shown on medical calendar tiles/list (may stack). */
const medicalEventIcons = ({ interpreter, customerEyesOn } = {}) => {
  const icons = [];
  if (isYesFlag(customerEyesOn)) icons.push('eyesOn');
  const language = normalizeLanguageSupportSelectValue(interpreter);
  if (language === 'Center') {
    icons.push('centerTranslate');
  } else if (
    language === 'Family'
    || language === 'Office'
    || language === 'Office(Phone)'
    || language === 'Nurse'
  ) {
    icons.push('nurseTranslate');
  }
  return icons;
};

const decorateMedicalCalendarVisuals = (item, { customer, resource } = {}) => {
  const medicalIcons = medicalEventIcons({
    interpreter: item?.data?.interpreter || item?.translation,
    customerEyesOn: customer?.eyes_on,
  });
  const transColorKey = medicalCalendarColorKeyFromTransportation(item?.data?.trans_method);
  const friendly =
    isFriendlyReminderProvider(item, resource) || transColorKey === 'friendly_reminder';
  const medicalColorKey = friendly ? 'friendly_reminder' : transColorKey;
  const medicalTileClass = medicalEventTileClassForColor(medicalColorKey);
  return {
    medicalIcons,
    isFriendlyReminder: friendly,
    medicalColorKey,
    medicalTileClass,
    _options: { additionalClasses: [medicalTileClass] },
  };
};

const medicalCalendarColorLegendOptions = [
  { value: 'blue', label: 'Default for By Center' },
  { value: 'pickup_dropoff', label: 'Pick-Up or Drop-Off Only' },
  { value: 'pink', label: 'Self-Transport' },
  { value: 'green', label: 'Televisit' },
  { value: 'black', label: 'Client Does Not Need to Go' },
  { value: 'friendly_reminder', label: 'Friendly Reminder' },
];

const medicalCalendarFilterColorOptions = [
  ...medicalCalendarColorLegendOptions,
  { value: 'gray', label: 'Deleted Appt' },
];

const medicalCalendarIconLegendOptions = [
  { value: 'red', icon: 'eyesOn', label: 'Eyes-On' },
  { value: 'teal', icon: 'nurseTranslate', label: 'Other Translation' },
  { value: 'brown', icon: 'centerTranslate', label: 'Center Translate' },
];

const isMedicalCalendarFilterSelected = (selectedColorFilters, value) => {
  const selected = (selectedColorFilters || []).map((f) => normalizeMedicalColor(f));
  if (value === 'pickup_dropoff') {
    return selected.some((f) => MEDICAL_PICKUP_DROPOFF_COLORS.has(f));
  }
  return selected.includes(normalizeMedicalColor(value));
};

const toggleMedicalCalendarFilter = (selectedColorFilters, value) => {
  const selected = [...(selectedColorFilters || [])];
  if (value === 'pickup_dropoff') {
    if (isMedicalCalendarFilterSelected(selected, value)) {
      return selected.filter((f) => !MEDICAL_PICKUP_DROPOFF_COLORS.has(normalizeMedicalColor(f)));
    }
    return [
      ...selected.filter((f) => !MEDICAL_PICKUP_DROPOFF_COLORS.has(normalizeMedicalColor(f))),
      'pickup_dropoff',
    ];
  }
  const key = normalizeMedicalColor(value);
  if (selected.map(normalizeMedicalColor).includes(key)) {
    return selected.filter((f) => normalizeMedicalColor(f) !== key);
  }
  return [...selected, value];
};

export const EventsService = {
  createNewEvent,
  getAllEvents,
  exportMedicalOneDayList,
  updateEvent,
  disableEvent,
  getEvent,
  deleteEvent,
  assignTransportationToEvents,
  formatDate,
  buildMedicalTransportationContext,
  getTransportationInfoFromContext,
  getTransportationInfo,
  generatePdf,
  generateSheet,
  generateDoc,
  generateSheetPDF,
  getTimeData,
  getByCustomer,
  customerHasMedicalAppointmentOnDate,
  site,
  matchesMedicalCalendarTypeFilter,
  dedupeMedicalDuplicateSlots,
  applyMedicalDeletedOnlyTileStyle,
  normalizeEventRecord,
  prepareEventsForScheduleX,
  medicalEventTileClassForColor,
  medicalEventIcons,
  decorateMedicalCalendarVisuals,
  medicalCalendarColorLegendOptions,
  medicalCalendarFilterColorOptions,
  medicalCalendarIconLegendOptions,
  isMedicalCalendarFilterSelected,
  toggleMedicalCalendarFilter,
  // Event Recurrence
  getAllEventRecurrences,
  createEventRecurrence,
  updateEventRecurrence,
  disableEventRecurrence,
  deleteEventRecurrence,
  // New option names
  languageSupportOptions,
  getMedicalLanguageSupportDisplay,
  normalizeLanguageSupportSelectValue,
  labelOptions,
  activityColorOptions,
  incidentColorOptions,
  mealPlanColorOptions,
  transportationTypeOptions,
  getMedicalTransportationSupportDisplay,
  normalizeTransportationSupportSelectValue,
  importantDatesLegendOptions,
  importantDatesFilterOptions,
  // Legacy aliases for backward compatibility
  interpreterLevelOptions,
  colorOptions
};
